/**
 * Stacked Bar Chart Type Definition
 */

import {
  paletteControl,
  numCategoriesControl,
  numStacksControl,
  labelRotationControl,
  truncateXAxisLabelsControl,
  gridControl,
  showValuesControl,
  valueDecimalsControl,
  axisControls,
  referenceLineControls,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  referenceLineCode,
  labelRotationCode,
  spineCode,
  finishCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornGridCode,
  seabornAxisLabelsCode,
  seabornYAxisRangeCode,
  seabornReferenceLineCode,
  seabornLabelRotationCode,
  seabornSpineCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';

import { getColors } from '../shared/palettes';
import { SERIES_HATCHES_MPL } from '../../utils/accessibility';

const showLegendControl = {
  key: 'showLegend',
  label: 'Show Legend',
  type: 'toggle',
};

const stackedBar = {
  id: 'stacked-bar',
  name: 'Stacked Bar Chart',
  category: 'bar',
  description: 'Show composition of totals across categories',
  
  sampleData: [
    { name: 'Cat 1', stack1: 45, stack2: 40, stack3: 30 },
    { name: 'Cat 2', stack1: 55, stack2: 35, stack3: 25 },
    { name: 'Cat 3', stack1: 35, stack2: 45, stack3: 30 },
    { name: 'Cat 4', stack1: 50, stack2: 35, stack3: 30 },
  ],
  
  defaultConfig: {
    externalColorMode: 'palette',
    useBluesPalette: true,
    useRedsPalette: false,
    useGreensPalette: false,
    numCategories: 4,
    numStacks: 3,
    outputFormat: 'matplotlib',
    labelRotation: null,
    truncateXAxisLabels: false,
    showGrid: true,
    showValues: false,
    valueDecimals: 0,
    ylabel: 'Value',
    xlabel: 'Category',
    title: '',
    yMin: '',
    yMax: '',
    showReferenceLine: false,
    referenceValue: '',
    referenceLabel: 'Target',
    usePatternFills: false,
    showLegend: true,
    colorOverrides: undefined,
  },
  
  controls: [
    paletteControl,
    numCategoriesControl,
    numStacksControl,
    labelRotationControl,
    truncateXAxisLabelsControl,
    gridControl,
    showValuesControl,
    valueDecimalsControl,
    {
      key: 'usePatternFills',
      label: 'Use Pattern Fills',
      type: 'toggle',
    },
    showLegendControl,
    ...axisControls,
    ...referenceLineControls,
  ],
  
  generateCode: (config, dataContext, s) => {
    const hasWideUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.inputShape === 'wide' && Array.isArray(dataContext.mapping?.valueColumns) && dataContext.mapping.valueColumns.length > 0;
    const hasLongUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.value && dataContext.mapping?.series;
    const hasUserData = hasWideUserData || hasLongUserData;
    const hasYMin = config.yMin && config.yMin.trim() !== '';
    const hasYMax = config.yMax && config.yMax.trim() !== '';
    const numCategories = config.numCategories || 4;
    const numStacks = config.numStacks || 3;
    
    const colors = getColors(config, hasWideUserData ? dataContext.mapping.valueColumns.length : numStacks, s).reverse();
    const hatches = JSON.stringify(SERIES_HATCHES_MPL);
    const usePatternFills = !!config.usePatternFills;
    
    // Generate category labels
    const categories = Array.from({ length: numCategories }, (_, i) => `Cat ${i + 1}`);
    
    // Seaborn output - Note: seaborn doesn't have native stacked bar, so we use pandas plot
    if (config.outputFormat === 'seaborn') {
      // Generate wide-form DataFrame for stacked bar
      const stackLines = [];
      for (let j = 0; j < numStacks; j++) {
        const values = Array.from({ length: numCategories }, () => Math.floor(20 + Math.random() * 40));
        stackLines.push(`    'Stack ${j + 1}': [${values.join(', ')}]`);
      }
      
      return `${seabornSetup(s)}

# ======== DATA ========
${hasUserData ? (hasWideUserData
  ? `df = pd.read_csv('${dataContext.fileName}')
df = df.set_index('${dataContext.mapping.category}')[${JSON.stringify(dataContext.mapping.valueColumns)}]`
  : `df = pd.read_csv('${dataContext.fileName}')
# Pivot to wide-form for stacked bar
df = df.pivot_table(index='${dataContext.mapping.category}', columns='${dataContext.mapping.series}', values='${dataContext.mapping.value}', aggfunc='first')`) : `# Wide-form DataFrame for stacked bar
df = pd.DataFrame({
    'category': ${JSON.stringify(categories)},
${stackLines.join(',\n')}
})
df = df.set_index('category')`}
# ======================
${seabornCreateFigure(5.5, 4.5, 300, s)}

# Active chart palette (${numStacks} interpolated colors)
colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

# Create stacked bar chart using pandas plot (seaborn doesn't have native stacked bar)
df.plot(kind='bar', stacked=True, color=colors, edgecolor='${s?.axisColor || '#000000'}',
        linewidth=1.0, ax=ax, width=0.7)
${config.usePatternFills ? `# Add non-color hatching by stack
for i, container in enumerate(ax.containers):
    for bar in container:
        bar.set_hatch(hatch_patterns[i % len(hatch_patterns)])` : '# Pattern fills disabled'}
${seabornTitleCode(config, s)}
${seabornAxisLabelsCode(config, s)}
${seabornLabelRotationCode(config)}

# Add grid
${seabornGridCode(config, 'y', s)}

${config.showValues ? `# Add value labels centered in each segment
for container in ax.containers:
    for bar in container:
        height = bar.get_height()
        if height > 0:
            txt_color = _coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}')
            ax.text(bar.get_x() + bar.get_width()/2., bar.get_y() + height/2.,
                    f'{height:.${config.valueDecimals || 0}f}',
                    ha='center', va='center', fontsize=${s?.valueLabelSize || 12}, color=txt_color)` : '# Value labels disabled'}

# Set y-axis range
${seabornYAxisRangeCode(config)}

${config.showLegend ? `# Customize legend
ax.legend(title='', loc='upper right', fontsize=${s?.legendSize || 10})` : `# Remove legend
ax.legend_.remove()`}
${seabornReferenceLineCode(config, 'horizontal', s)}
${seabornSpineCode(s, 'label', 'number')}
${seabornFinishCode('stacked_bar_chart')}`;
    }
    
    // Matplotlib output (default)
    // Generate stack data
    const stackCode = Array.from({ length: numStacks }, (_, i) => {
      const values = Array.from({ length: numCategories }, () => Math.floor(20 + Math.random() * 40));
      return `stack${i + 1} = [${values.join(', ')}]  # Stack ${i + 1} values`;
    }).join('\n');
    
    // Generate bar creation code with proper bottom stacking
    const barCodes = Array.from({ length: numStacks }, (_, i) => {
      if (i === 0) {
        return `bars${i + 1} = ax.bar(categories, stack${i + 1}, label='Stack ${i + 1}', color=colors[${i}], hatch=(hatch_patterns[${i} % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))`;
      } else {
        const bottomParts = Array.from({ length: i }, (_, j) => `np.array(stack${j + 1})`).join(' + ');
        return `bars${i + 1} = ax.bar(categories, stack${i + 1}, bottom=${bottomParts}, label='Stack ${i + 1}', color=colors[${i}], hatch=(hatch_patterns[${i} % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))`;
      }
    }).join('\n');
    
    // Generate bars list for value labels
    const barsList = Array.from({ length: numStacks }, (_, i) => `bars${i + 1}`).join(', ');

    if (hasUserData) {
      return `${matplotlibSetup(s)}

# ======== DATA ========
df = pd.read_csv('${dataContext.fileName}')
${hasWideUserData
  ? `pivot = df.set_index('${dataContext.mapping.category}')[${JSON.stringify(dataContext.mapping.valueColumns)}]`
  : `pivot = df.pivot_table(index='${dataContext.mapping.category}', columns='${dataContext.mapping.series}', values='${dataContext.mapping.value}', aggfunc='first')`}
categories = pivot.index.tolist()
stack_names = pivot.columns.tolist()
# ======================
${createFigure(5.5, 4.5, 300, s)}

# Active chart palette
colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

# Create stacked bars
bottom = np.zeros(len(categories))
for idx, stack_name in enumerate(stack_names):
    vals = pivot[stack_name].values
    ax.bar(categories, vals, bottom=bottom, label=stack_name, color=colors[idx % len(colors)],
           hatch=(hatch_patterns[idx % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))
    bottom += vals

${config.showValues ? `# Add value labels centered in each segment
for container in ax.containers:
    for bar in container:
        height = bar.get_height()
        if height > 0:
            ax.text(bar.get_x() + bar.get_width()/2., bar.get_y() + height/2.,
                    f'{height:.${config.valueDecimals || 0}f}',
                    ha='center', va='center', fontsize=${s?.valueLabelSize || 12},
                    color=_coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}'))` : '# Value labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${labelRotationCode(config)}

# Add grid
${gridCode(config, 'y', s)}

# Set y-axis range
${hasYMin || hasYMax ? `ax.set_ylim(${hasYMin ? `bottom=${config.yMin}` : ''}${hasYMin && hasYMax ? ', ' : ''}${hasYMax ? `top=${config.yMax}` : ''})` : '# Auto y-axis range'}

${config.showLegend ? `# Add legend
ax.legend(loc='upper right', fontsize=${s?.legendSize || 10})` : '# Legend disabled'}
${referenceLineCode(config, 'horizontal', s)}
${spineCode(s, 'label', 'number')}
${finishCode('stacked_bar_chart')}`;
    }

    return `${matplotlibSetup(s)}

# ======== DATA ========
categories = ${JSON.stringify(categories)}  # Category labels
${stackCode}
# ======================
${createFigure(5.5, 4.5, 300, s)}

# Active chart palette (${numStacks} interpolated colors)
colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

# Create stacked bars
${barCodes}

${config.showValues ? `# Add value labels centered in each segment
for bars in [${barsList}]:
    for bar in bars:
        height = bar.get_height()
        if height > 0:
            ax.text(bar.get_x() + bar.get_width()/2., bar.get_y() + height/2.,
                    f'{height:.${config.valueDecimals || 0}f}',
                    ha='center', va='center', fontsize=${s?.valueLabelSize || 12},
                    color=_coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}'))` : '# Value labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${labelRotationCode(config)}

# Add grid
${gridCode(config, 'y', s)}

# Set y-axis range
${hasYMin || hasYMax ? `ax.set_ylim(${hasYMin ? `bottom=${config.yMin}` : ''}${hasYMin && hasYMax ? ', ' : ''}${hasYMax ? `top=${config.yMax}` : ''})` : '# Auto y-axis range'}

${config.showLegend ? `# Add legend
ax.legend(loc='upper right', fontsize=${s?.legendSize || 10})` : '# Legend disabled'}
${referenceLineCode(config, 'horizontal', s)}
${spineCode(s, 'label', 'number')}
${finishCode('stacked_bar_chart')}`;
  },
};

export default stackedBar;
